import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';

const GitHubActivity = () => {
  const [selectedMonth, setSelectedMonth] = useState(null);

  // Mock GitHub contribution data
  const generateContributionData = () => {
    const data = [];
    const startDate = new Date();
    startDate.setFullYear(startDate.getFullYear() - 1);
    
    for (let i = 0; i < 365; i++) {
      const date = new Date(startDate);
      date.setDate(date.getDate() + i);
      
      // Generate realistic contribution patterns
      const dayOfWeek = date.getDay();
      const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
      const baseContributions = isWeekend ? Math.random() * 3 : Math.random() * 8;
      
      data.push({
        date: date.toISOString().split('T')[0],
        count: Math.floor(baseContributions),
        level: Math.floor(baseContributions / 2)
      });
    }
    return data;
  };

  const [contributionData] = useState(generateContributionData());

  const getContributionColor = (level) => {
    const colors = [
      'bg-muted',
      'bg-green-200',
      'bg-green-400',
      'bg-green-600',
      'bg-green-800'
    ];
    return colors[Math.min(level, 4)];
  };

  const totalContributions = contributionData.reduce((sum, day) => sum + day.count, 0);
  const currentStreak = 47; // Mock current streak
  const longestStreak = 89; // Mock longest streak

  const stats = [
    {
      label: 'Total Contributions',
      value: totalContributions.toLocaleString(),
      icon: 'GitCommit',
      color: 'text-primary'
    },
    {
      label: 'Current Streak',
      value: `${currentStreak} days`,
      icon: 'Flame',
      color: 'text-orange-500'
    },
    {
      label: 'Longest Streak',
      value: `${longestStreak} days`,
      icon: 'Trophy',
      color: 'text-yellow-500'
    },
    {
      label: 'Active Repositories',
      value: '24',
      icon: 'Folder',
      color: 'text-green-500'
    }
  ];

  // Group contributions by weeks
  const groupByWeeks = (data) => {
    const weeks = [];
    let currentWeek = [];
    
    data.forEach((day, index) => {
      const date = new Date(day.date);
      const dayOfWeek = date.getDay();
      
      if (dayOfWeek === 0 && currentWeek.length > 0) {
        weeks.push(currentWeek);
        currentWeek = [];
      }
      
      currentWeek.push(day);
      
      if (index === data.length - 1) {
        weeks.push(currentWeek);
      }
    });
    
    return weeks;
  };

  const weeks = groupByWeeks(contributionData);

  return (
    <section className="py-20 bg-surface">
      <div className="container mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl lg:text-5xl font-bold text-text-primary mb-4">
            GitHub Activity
          </h2>
          <p className="text-lg text-text-secondary max-w-2xl mx-auto">
            Consistent coding activity and open-source contributions demonstrating continuous learning and development
          </p>
        </motion.div>

        <div className="max-w-6xl mx-auto">
          {/* Stats Grid */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-12"
          >
            {stats.map((stat, index) => (
              <div key={index} className="bg-white rounded-xl p-6 border border-border shadow-sm">
                <div className="flex items-center space-x-3 mb-2">
                  <Icon name={stat.icon} size={20} className={stat.color} />
                  <span className="text-sm text-text-secondary font-medium">{stat.label}</span>
                </div>
                <p className="text-2xl font-bold text-text-primary">{stat.value}</p>
              </div>
            ))}
          </motion.div>

          {/* Contribution Graph */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="bg-white rounded-xl p-6 border border-border shadow-sm"
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-text-primary">
                Contribution Activity
              </h3>
              <div className="flex items-center space-x-2 text-sm text-text-secondary">
                <span>Less</span>
                <div className="flex space-x-1">
                  {[0, 1, 2, 3, 4].map((level) => (
                    <div
                      key={level}
                      className={`w-3 h-3 rounded-sm ${getContributionColor(level)}`}
                    />
                  ))}
                </div>
                <span>More</span>
              </div>
            </div>

            {/* Contribution Grid */}
            <div className="overflow-x-auto">
              <div className="inline-flex space-x-1 min-w-full">
                {weeks.map((week, weekIndex) => (
                  <div key={weekIndex} className="flex flex-col space-y-1">
                    {Array.from({ length: 7 }, (_, dayIndex) => {
                      const day = week[dayIndex];
                      return (
                        <motion.div
                          key={`${weekIndex}-${dayIndex}`}
                          className={`w-3 h-3 rounded-sm cursor-pointer transition-premium ${
                            day ? getContributionColor(day.level) : 'bg-muted'
                          }`}
                          whileHover={{ scale: 1.2 }}
                          title={day ? `${day.count} contributions on ${day.date}` : ''}
                        />
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>

            {/* Month Labels */}
            <div className="flex justify-between mt-4 text-xs text-text-muted">
              <span>Jan</span>
              <span>Feb</span>
              <span>Mar</span>
              <span>Apr</span>
              <span>May</span>
              <span>Jun</span>
              <span>Jul</span>
              <span>Aug</span>
              <span>Sep</span>
              <span>Oct</span>
              <span>Nov</span>
              <span>Dec</span>
            </div>
          </motion.div>

          {/* Recent Activity */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="mt-8 bg-white rounded-xl p-6 border border-border shadow-sm"
          >
            <h3 className="text-xl font-semibold text-text-primary mb-6">
              Recent Activity
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                {
                  type: 'commit',
                  repo: 'portfolio-website',
                  message: 'Add responsive design improvements',
                  time: '2 hours ago'
                },
                {
                  type: 'pr',
                  repo: 'react-dashboard',
                  message: 'Implement dark mode toggle feature',
                  time: '1 day ago'
                },
                {
                  type: 'issue',
                  repo: 'open-source-project',
                  message: 'Fix authentication bug in login flow',
                  time: '2 days ago'
                },
                {
                  type: 'commit',
                  repo: 'e-commerce-platform',
                  message: 'Optimize database queries for faster performance',
                  time: '3 days ago'
                },
                {
                  type: 'pr',
                  repo: 'mobile-app-backend',
                  message: 'Add real-time notification system with WebSocket',
                  time: '4 days ago'
                },
                {
                  type: 'commit',
                  repo: 'analytics-dashboard',
                  message: 'Integrate advanced data visualization charts',
                  time: '5 days ago'
                }
              ].map((activity, index) => (
                <motion.div 
                  key={index} 
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ scale: 1.03, y: -4 }}
                  className="flex items-start space-x-3 p-4 rounded-lg border border-border bg-white hover:bg-surface hover:border-purple-300 transition-all duration-300 cursor-pointer shadow-sm hover:shadow-md"
                >
                  <div className="w-10 h-10 rounded-md bg-gradient-to-br from-purple-100 to-blue-100 flex items-center justify-center flex-shrink-0">
                    <Icon 
                      name={activity.type === 'commit' ? 'GitCommit' : activity.type === 'pr' ? 'GitPullRequest' : 'AlertCircle'} 
                      size={18} 
                      className="text-purple-600" 
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-text-primary mb-1 line-clamp-2">
                      {activity.message}
                    </p>
                    <div className="flex items-center space-x-2 text-xs text-text-muted">
                      <span className="font-mono truncate">{activity.repo}</span>
                      <span>•</span>
                      <span>{activity.time}</span>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default GitHubActivity;